/**
 * The time a publish has left, from what the world's last publishes took (ADR-086's amendment of 2026-10-13,
 * decision 5). Pure: it takes the remembered phases and where this publish is, and answers a length of time.
 */

import { PUBLISH_PHASES } from "./protocol-publish.ts";
import type { PublishPhase } from "./protocol-publish.ts";

/**
 * How long each phase of one publish took, as `recordPublish` writes it in the ledger entry of a publish:
 * whole milliseconds, and for the two phases that count their work the total they reported.
 */
export interface PublishPhases {
  readonly compile: number;
  readonly assets: { readonly ms: number; readonly total: number };
  readonly world: { readonly ms: number; readonly total: number };
  readonly pointer: number;
}

/**
 * Where a publish is, and what is remembered of the ones before it.
 */
export interface EstimateInput {
  /** What earlier publishes took, newest first; only the first `PUBLISH_MEMORY` are read. */
  readonly remembered: readonly PublishPhases[];
  readonly phase: PublishPhase;
  /** The work this phase has done, whole, not above `total`. */
  readonly done: number;
  /** The work this phase reported; 1 with nothing done is the route's placeholder before the real count is known. */
  readonly total: number;
  /** Milliseconds since this phase began. */
  readonly inPhase: number;
  /** Milliseconds since the publish took the lock. */
  readonly elapsed: number;
}

/**
 * The estimate: the time left in whole seconds as milliseconds, or null for none, and whether the publish has run
 * well past what was remembered.
 */
export interface PublishEstimate {
  readonly left: number | null;
  readonly late: boolean;
}

/**
 * How many remembered publishes are read (set).
 */
export const PUBLISH_MEMORY = 5;

/**
 * How many remembered publishes there must be for a time to be given (set; the author's open question 1).
 */
export const PUBLISH_MIN_REMEMBERED = 3;

/**
 * The most a time left can say, in milliseconds (set).
 */
export const PUBLISH_LEFT_MAX_MS = 600_000;

/**
 * The share of a phase that must be done before this publish's pace is read (set).
 */
export const PUBLISH_PACE_SHARE = 0.1;

/**
 * The milliseconds a phase must have run before this publish's pace is read (set).
 */
export const PUBLISH_PACE_AFTER_MS = 2_000;

/**
 * The least and the most the pace scale can be (set).
 */
export const PUBLISH_PACE_MIN = 0.5;
export const PUBLISH_PACE_MAX = 4;

/**
 * A publish is late past this factor times the remembered total plus `PUBLISH_LATE_SLACK_MS` (set).
 */
export const PUBLISH_LATE_FACTOR = 1.5;

/**
 * The slack added to the late limit, in milliseconds (set).
 */
export const PUBLISH_LATE_SLACK_MS = 10_000;

/**
 * Whether a value is a whole number of milliseconds or a count: finite, an integer, not negative.
 *
 * @param value The value.
 * @returns True when it is.
 */
function whole(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Reads a recorded `phases` value back.
 *
 * @param value The `phases` a publish recorded, or anything.
 * @returns The phases, or null when it is not exactly the recorded shape with whole, non-negative numbers.
 */
export function parsePublishPhases(value: unknown): PublishPhases | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const keys = Object.keys(value);
  const phases = value as { readonly compile?: unknown; readonly assets?: unknown; readonly world?: unknown; readonly pointer?: unknown };
  if (keys.length !== PUBLISH_PHASES.length || !PUBLISH_PHASES.every((key) => keys.includes(key))) {
    return null;
  }
  const counted = (part: unknown): { readonly ms: number; readonly total: number } | null => {
    if (typeof part !== "object" || part === null || Array.isArray(part)) {
      return null;
    }
    const { ms, total } = part as { readonly ms?: unknown; readonly total?: unknown };
    return Object.keys(part).length === 2 && whole(ms) && whole(total) ? { ms, total } : null;
  };
  const assets = counted(phases.assets);
  const world = counted(phases.world);
  if (!whole(phases.compile) || !whole(phases.pointer) || assets === null || world === null) {
    return null;
  }
  return { compile: phases.compile, assets, world, pointer: phases.pointer };
}

/**
 * The median of some numbers.
 *
 * @param values One or more numbers.
 * @returns The middle one, or the mean of the middle two rounded down.
 */
function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  return sorted.length % 2 === 1 ? (sorted[middle] as number) : Math.floor(((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2);
}

/**
 * The median time of each phase over some publishes.
 *
 * @param publishes One or more publishes.
 * @returns The milliseconds each phase usually takes.
 */
function usual(publishes: readonly PublishPhases[]): Readonly<Record<PublishPhase, number>> {
  return {
    compile: median(publishes.map((p) => p.compile)),
    assets: median(publishes.map((p) => p.assets.ms)),
    world: median(publishes.map((p) => p.world.ms)),
    pointer: median(publishes.map((p) => p.pointer)),
  };
}

/**
 * What is left of the phase a publish is in.
 *
 * @param phase Which.
 * @param usualMs What the phase usually takes.
 * @param input Where the publish is.
 * @returns The milliseconds left of it.
 */
function remainder(phase: PublishPhase, usualMs: number, input: EstimateInput): number {
  const counted = (phase === "assets" || phase === "world") && !(input.total === 1 && input.done === 0);
  if (!counted) {
    return Math.max(0, usualMs - input.inPhase);
  }
  if (input.total === 0) {
    return 0;
  }
  const share = input.done / input.total;
  const paced = share >= PUBLISH_PACE_SHARE && input.inPhase >= PUBLISH_PACE_AFTER_MS && usualMs > 0;
  const scale = paced ? Math.min(PUBLISH_PACE_MAX, Math.max(PUBLISH_PACE_MIN, input.inPhase / share / usualMs)) : 1;
  return usualMs * (1 - share) * scale;
}

/**
 * The time a publish has left.
 *
 * @param input What earlier publishes took and where this one is.
 * @returns With fewer than `PUBLISH_MIN_REMEMBERED` remembered, none and not late. Past 1.5 times the remembered
 *   total plus 10 seconds, late and none. Otherwise the remainder of the current phase (the compile and the pointer
 *   have no count, so their usual time less the time spent; a counted phase its share not yet done, scaled by this
 *   publish's pace once 10 percent and 2 seconds are done, the scale clamped to 0.5 to 4; a `total` of 1 with
 *   nothing done is the route's placeholder and is not used) plus the usual time of each later phase, rounded
 *   down to a whole second and clamped to 600,000 ms.
 * @throws RangeError when a time or a count is not a whole non-negative number, or `done` passes `total`.
 */
export function estimatePublish(input: EstimateInput): PublishEstimate {
  if (!whole(input.done) || !whole(input.total) || input.done > input.total || !Number.isFinite(input.inPhase) || input.inPhase < 0 || !Number.isFinite(input.elapsed) || input.elapsed < 0) {
    throw new RangeError("a publish's progress is whole counts with done not above total, and times that are not negative");
  }
  const publishes = input.remembered.slice(0, PUBLISH_MEMORY);
  if (publishes.length < PUBLISH_MIN_REMEMBERED) {
    return { left: null, late: false };
  }
  const times = usual(publishes);
  const total = PUBLISH_PHASES.reduce((sum, phase) => sum + times[phase], 0);
  if (input.elapsed > PUBLISH_LATE_FACTOR * total + PUBLISH_LATE_SLACK_MS) {
    return { left: null, late: true };
  }
  const at = PUBLISH_PHASES.indexOf(input.phase);
  const later = PUBLISH_PHASES.slice(at + 1).reduce((sum, phase) => sum + times[phase], 0);
  const left = remainder(input.phase, times[input.phase], input) + later;
  return { left: Math.min(PUBLISH_LEFT_MAX_MS, Math.floor(left / 1000) * 1000), late: false };
}
